import { CUSTOMER_ENDPOINTS, apiFetch, getAuthHeaders, parseResponse } from "./config.js";
import { notifySessionExpired } from "@/auth/session.js";
import { downloadPdfBlob } from "@/utils/downloadPdf.js";

export async function getUser() {
    const response = await apiFetch(CUSTOMER_ENDPOINTS.USER, { headers: getAuthHeaders() });
    return parseResponse(response);
}

export async function updateUser(payload) {
    const response = await apiFetch(CUSTOMER_ENDPOINTS.USER, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
    });
    return parseResponse(response);
}

export async function changePassword(payload) {
    const response = await apiFetch(CUSTOMER_ENDPOINTS.CHANGE_PASSWORD, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
    });
    return parseResponse(response);
}

export async function completeCustomerWalkthrough() {
    const response = await apiFetch(CUSTOMER_ENDPOINTS.WALKTHROUGH_COMPLETE, {
        method: "POST",
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}

export async function getCart() {
    const response = await apiFetch(CUSTOMER_ENDPOINTS.CART, { headers: getAuthHeaders() });
    return parseResponse(response);
}

export async function addCartItem(productId, quantity = 1) {
    const response = await apiFetch(CUSTOMER_ENDPOINTS.CART_ITEMS, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ productId, quantity })
    });
    return parseResponse(response);
}

export async function updateCartItem(productId, quantity) {
    const response = await apiFetch(CUSTOMER_ENDPOINTS.CART_ITEM(productId), {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({ quantity })
    });
    return parseResponse(response);
}

export async function removeCartItem(productId) {
    const response = await apiFetch(CUSTOMER_ENDPOINTS.CART_ITEM(productId), {
        method: "DELETE",
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}

export async function getLikedItems({ offset = 0, limit } = {}) {
    const params = new URLSearchParams();
    params.set("offset", String(offset));
    if (limit != null) {
        params.set("limit", String(limit));
    }
    const response = await apiFetch(`${CUSTOMER_ENDPOINTS.LIKED_ITEMS}?${params}`, { headers: getAuthHeaders() });
    return parseResponse(response);
}

export async function getPurchasedProducts({ offset = 0, limit } = {}) {
    const params = new URLSearchParams();
    params.set("offset", String(offset));
    if (limit != null) {
        params.set("limit", String(limit));
    }
    const response = await apiFetch(`${CUSTOMER_ENDPOINTS.PURCHASED_PRODUCTS}?${params}`, {
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}

export async function getLikedItemIds() {
    const response = await apiFetch(CUSTOMER_ENDPOINTS.LIKED_ITEM_IDS, { headers: getAuthHeaders() });
    return parseResponse(response);
}

export async function addLikedItem(productId) {
    const response = await apiFetch(CUSTOMER_ENDPOINTS.LIKED_ITEM_ITEMS, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ productId })
    });
    return parseResponse(response);
}

export async function removeLikedItem(productId) {
    const response = await apiFetch(CUSTOMER_ENDPOINTS.LIKED_ITEM(productId), {
        method: "DELETE",
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}

export async function getRecommendations({ offset = 0, limit, seed, excludeCart = false, excludeProductId } = {}) {
    const params = new URLSearchParams();
    params.set("offset", String(offset));
    if (limit != null) {
        params.set("limit", String(limit));
    }
    if (seed) {
        params.set("seed", seed);
    }
    if (excludeCart) {
        params.set("excludeCart", "true");
    }
    if (excludeProductId) {
        params.set("excludeProductId", String(excludeProductId));
    }
    const response = await apiFetch(
        `${CUSTOMER_ENDPOINTS.RECOMMENDATIONS}?${params.toString()}`,
        { headers: getAuthHeaders() }
    );
    return parseResponse(response, "getRecommendations");
}

export async function checkoutOrder(payload) {
    const response = await apiFetch(CUSTOMER_ENDPOINTS.CHECKOUT, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
    });
    return parseResponse(response, "checkoutOrder");
}

export async function getOrders() {
    const response = await apiFetch(CUSTOMER_ENDPOINTS.ORDERS, { headers: getAuthHeaders() });
    return parseResponse(response);
}

export async function getOrder(orderId) {
    const response = await apiFetch(CUSTOMER_ENDPOINTS.ORDER(orderId), { headers: getAuthHeaders() });
    return parseResponse(response);
}

export async function updateOrder(orderId, payload) {
    const response = await apiFetch(CUSTOMER_ENDPOINTS.ORDER(orderId), {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
    });
    return parseResponse(response, "updateOrder");
}

export async function cancelOrder(orderId) {
    const response = await apiFetch(CUSTOMER_ENDPOINTS.ORDER_CANCEL(orderId), {
        method: "POST",
        headers: getAuthHeaders()
    });
    return parseResponse(response, "cancelOrder");
}

export async function downloadOrderReceipt(orderId) {
    const response = await apiFetch(CUSTOMER_ENDPOINTS.ORDER_RECEIPT(orderId), {
        headers: getAuthHeaders(false)
    });
    if (response.status === 401 && localStorage.getItem("token")) {
        notifySessionExpired();
        throw new Error("Session expirée, veuillez vous reconnecter");
    }
    if (!response.ok) {
        let message = `Erreur ${response.status}`;
        try {
            const data = await response.json();
            message = data.errors?.[0] || message;
        } catch {
            // PDF or empty body
        }
        throw new Error(message);
    }
    const blob = await response.blob();
    const disposition = response.headers.get("Content-Disposition") || "";
    const match = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)"?/i);
    const rawName = match?.[1] ? decodeURIComponent(match[1].replace(/"/g, "")) : `recu-${orderId}.pdf`;
    await downloadPdfBlob(blob, rawName);
}

export async function getAddresses() {
    const response = await apiFetch(CUSTOMER_ENDPOINTS.ADDRESSES, { headers: getAuthHeaders() });
    return parseResponse(response);
}

export async function createAddress(payload) {
    const response = await apiFetch(CUSTOMER_ENDPOINTS.ADDRESSES, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
    });
    return parseResponse(response);
}

export async function updateAddress(addressId, payload) {
    const response = await apiFetch(CUSTOMER_ENDPOINTS.ADDRESS(addressId), {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
    });
    return parseResponse(response);
}

export async function deleteAddress(addressId) {
    const response = await apiFetch(CUSTOMER_ENDPOINTS.ADDRESS(addressId), {
        method: "DELETE",
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}

export async function uploadCustomerFile(file) {
    const formData = new FormData();
    formData.append("file", file);
    const response = await apiFetch(CUSTOMER_ENDPOINTS.UPLOAD_FILE, {
        method: "POST",
        headers: getAuthHeaders(false),
        body: formData
    });
    return parseResponse(response);
}

export async function getProductReviewEligibility(productId) {
    const response = await apiFetch(CUSTOMER_ENDPOINTS.PRODUCT_REVIEWS(productId), {
        headers: getAuthHeaders()
    });
    return parseResponse(response);
}

export async function createProductReview(productId, payload) {
    const response = await apiFetch(CUSTOMER_ENDPOINTS.PRODUCT_REVIEWS(productId), {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
    });
    return parseResponse(response);
}
